// Story 1.26 — creator dashboard at /account/creator-dashboard.
//
// Tabbed scaffold with 3 panels:
//   * Earnings — WIRED-with-zero-data on day one. Renders the
//     <CreatorEarningsTicker> structural artifact per UX-DR21 + PRD
//     §Tama journey. Default tab so the screenshot-magnet is what
//     the creator sees first.
//   * Applications — WIRED. Reuses Story 1.21's creator-application
//     summary + Story 1.24's portfolio-applications list under one
//     panel via the dashboard endpoint's aggregation.
//   * Dossiers — UNWIRED placeholder card (Story 3.X dependency).
//
// Wrapped by <ApprovedCreatorGuard> at the App.tsx route level. The
// backend's enforce_creator_status_is_approved is the real
// authorization boundary; the SPA guard is a UX-redirect convenience.
//
// `isWired<T>(panel)` + `PlaceholderPanel` are RE-DECLARED locally
// (not cross-imported from AdminMetricsPage.tsx) per the
// feature-folder isolation convention. Re-declaration cost is 22
// lines; cross-import would couple admin + creator surfaces.

import {
  Badge,
  Button,
  Card,
  CardContent,
  CreatorEarningsTicker,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@greenhold/components";

import { ApplicationsPanel } from "../features/creator-portfolios/ApplicationsPanel";
import { CreatorPayoutPanel } from "../features/creator-payout/CreatorPayoutPanel";
import { CreatorTosBanner } from "../features/creator-dashboard/CreatorTosBanner";
import { DossiersPanel } from "../features/creator-dashboard/DossiersPanel";
import { LinkedAccountsSection } from "../features/creator-dashboard/LinkedAccountsSection";
import { NewPostCard } from "../features/creator-dashboard/NewPostCard";
import {
  type CreatorDashboardEnvelope,
  type CreatorPanelEnvelope,
  type EarningsPanelData,
  useCreatorDashboard,
} from "../features/creator-dashboard/useCreatorDashboard";
import { useCreatorProfile } from "../features/creator-profile/useCreatorProfile";

function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function isWired<T>(
  panel: CreatorPanelEnvelope<T>,
): panel is { data: T } {
  return "data" in panel;
}

function PlaceholderPanel({
  reason,
  story,
}: {
  reason: string;
  story: string;
}) {
  return (
    <Card>
      <CardContent className="p-8 text-center space-y-3">
        <Badge variant="secondary" className="font-mono">
          Coming with Story {story}
        </Badge>
        <p className="opacity-80 max-w-xl mx-auto leading-relaxed">{reason}</p>
        <p className="text-xs opacity-60 italic">
          No mock data is shown — this panel lights up when its source story
          ships.
        </p>
      </CardContent>
    </Card>
  );
}

function EarningsTickerPanel({ data }: { data: EarningsPanelData }) {
  return (
    <div className="space-y-4">
      <div className="max-w-xl">
        <CreatorEarningsTicker
          state={data.cumulative_eur_cents === 0 ? "empty" : "default"}
          cumulativeEurCents={data.cumulative_eur_cents}
          currentMonthEurCents={data.current_month_eur_cents}
          primaryEurCents={data.primary_eur_cents}
          resaleEurCents={data.resale_eur_cents}
          holdersCount={data.holders_count}
          sparkline4w={data.sparkline_4w}
        />
      </div>
      {data.cumulative_eur_cents === 0 ? (
        <Card>
          <CardContent className="p-6 text-sm opacity-80">
            <p>
              The ticker structure renders today; numeric values populate
              when Epic 3 primary-sale ledger entries + Epic 5 marketplace
              royalties accrue.
            </p>
            {data.period_start ? (
              <p className="mt-2 text-xs opacity-70">
                Earnings tracked since {new Date(data.period_start).toLocaleDateString()}
                .
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

export function CreatorDashboardPage() {
  const query = useCreatorDashboard();
  // Story 1.34 — drives the creator-terms banner. Deliberately NOT gating the
  // page render on it: a creator with stale terms must still see everything
  // here, including their earnings and the dossiers they appear in.
  const profileQuery = useCreatorProfile();

  if (query.isPending) {
    return (
      <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <Skeleton className="h-10 w-64 mb-3" />
          <Skeleton className="h-4 w-96 mb-8" />
          <Skeleton className="h-72 w-full rounded-lg" />
        </div>
      </main>
    );
  }

  if (query.isError) {
    return (
      <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <Card>
            <CardContent className="p-6 space-y-3">
              <p className="text-destructive">
                Couldn't load your dashboard: {query.error.message}
              </p>
              <Button variant="secondary" onClick={() => query.refetch()}>
                Retry
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>
    );
  }

  const envelope: CreatorDashboardEnvelope = query.data.data;

  return (
    <main className="min-h-screen bg-brand-paper-200 text-brand-forest-900">
      <div className="max-w-6xl mx-auto px-6 py-10">
        <header className="mb-8 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">
              Your creator dashboard
            </h1>
            <p className="opacity-80">
              Your applications pipeline, your earnings ticker, and the
              dossiers you appear in — all in one place.
            </p>
          </div>
          <p className="text-xs uppercase tracking-wider font-mono opacity-60">
            Updated {formatRelative(envelope.generated_at)}
          </p>
        </header>

        {/* Story 1.34 — the ONLY place the creator-terms prompt appears. It
            is intentionally not a session gate and not shown on any other
            surface; see CreatorTosBanner for why. Rendered above the authoring
            surface because publishing is one of the actions it blocks. */}
        {profileQuery.data?.data.creator_tos_status === "needs_acceptance" ? (
          <div className="mb-6">
            <CreatorTosBanner
              versionLabel={profileQuery.data.data.creator_tos_version_label}
            />
          </div>
        ) : null}

        {/* Story 7.10 — the authoring surface, first section on the page. */}
        <NewPostCard />

        <Tabs defaultValue="earnings" className="w-full">
          <TabsList>
            <TabsTrigger value="earnings">Earnings</TabsTrigger>
            <TabsTrigger value="applications">Applications</TabsTrigger>
            <TabsTrigger value="dossiers">Dossiers</TabsTrigger>
            {/* Story 8.5 — the payout rail. It lives on the CREATOR dashboard
                and nowhere else: this is the one place on Greenhold where
                money leaves, and it leaves as supplier income for content
                supplied. There is no user-facing equivalent. */}
            <TabsTrigger value="payouts">Payouts</TabsTrigger>
          </TabsList>

          <TabsContent value="earnings" className="mt-6">
            {isWired(envelope.earnings) ? (
              <EarningsTickerPanel data={envelope.earnings.data} />
            ) : (
              <PlaceholderPanel
                reason={envelope.earnings.unavailable_reason}
                story={envelope.earnings.populates_in_story}
              />
            )}
          </TabsContent>

          <TabsContent value="applications" className="mt-6">
            {isWired(envelope.applications) ? (
              <ApplicationsPanel data={envelope.applications.data} />
            ) : (
              <PlaceholderPanel
                reason={envelope.applications.unavailable_reason}
                story={envelope.applications.populates_in_story}
              />
            )}
          </TabsContent>

          <TabsContent value="dossiers" className="mt-6">
            {isWired(envelope.dossiers) ? (
              <DossiersPanel data={envelope.dossiers.data} />
            ) : (
              <PlaceholderPanel
                reason={envelope.dossiers.unavailable_reason}
                story={envelope.dossiers.populates_in_story}
              />
            )}
          </TabsContent>

          {/* Story 8.5 — the earnings pot, the payout request, the free
              conversion, the settlement destination and the invoice archive.
              Fetches on its own rather than through the dashboard envelope: it
              is a money surface and must not render a stale pot figure baked
              into a page load. */}
          <TabsContent value="payouts" className="mt-6">
            <CreatorPayoutPanel />
          </TabsContent>
        </Tabs>

        {/* Story 7.8 — Twitch (and future external-account) linkage. */}
        <LinkedAccountsSection />
      </div>
    </main>
  );
}
